import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import ConfirmModal from "../../../components/Modals/ConfirmModal";
import { toast } from "../../../components/Toast/toast";
import { btn } from "../../../components/Button/buttonStyles";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "—";

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString();
};

const Field = ({ label, value, children }) => (
  <div className="flex">
    <span className="w-44 font-medium text-gray-700 shrink-0">{label}:</span>
    <span className="text-gray-900">{children ?? value ?? "—"}</span>
  </div>
);

const ReceivableDetailPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || "flat_rate";
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const endpoint =
    type === "unit_based"
      ? `/project-finances/unit-based-receivables/${id}`
      : `/project-finances/flat-rate-receivables/${id}`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await api.get(endpoint, { withCredentials: true });
        setData(res.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to load receivable details.");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchData();
  }, [id, endpoint]);

  const handleDelete = async () => {
    try {
      await api.delete(endpoint, { withCredentials: true });
      navigate("/client/receivables");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to delete receivable.");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handleEdit = () => {
    const path =
      type === "unit_based"
        ? `/project/${data.project_id}/edit-unit-based-receivable/${id}`
        : `/project/${data.project_id}/edit-flat-rate-receivable/${id}`;
    navigate(path);
  };

  const handleClone = async () => {
    const cloneEndpoint =
      type === "unit_based"
        ? `/project-finances/unit-based-receivables`
        : `/project-finances/flat-rate-receivables`;

    const {
      id: _id, createdAt, updatedAt,
      project, service, languagePair, currency, unit, file,
      ...payload
    } = data;

    try {
      const res = await api.post(cloneEndpoint, { ...payload, project_id: Number(data.project_id) }, { withCredentials: true });
      if (res.data.success) {
        navigate(`/client/receivable/${res.data.data.id}?type=${type}`);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to clone receivable.");
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center mt-16">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  if (error)
    return (
      <div className="text-center mt-10 text-danger-600 font-medium">{error}</div>
    );
  if (!data)
    return (
      <div className="text-center mt-10 text-gray-500">
        Receivable not found.
      </div>
    );

  const client = data.project?.client;
  const clientName = client
    ? client.type?.toLowerCase() === "company"
      ? client.company_name || "—"
      : client.primary_user
      ? `${client.primary_user.first_name} ${client.primary_user.last_name}`.trim()
      : "—"
    : "—";

  const langPair = data.languagePair
    ? `${data.languagePair.sourceLanguage?.name || ""} - ${
        data.languagePair.targetLanguage?.name || ""
      }`
    : "—";

  const currCode = data.currency?.currency?.code || "—";
  const code = data.receivable_code || "";
  const isInvoiced = !!data.invoice;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <BackButton />
          <h2 className="text-[22px] font-semibold tracking-tight text-gray-900">
            Receivable {code}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleClone}
            className={btn("secondary", "sm")}
          >
            Clone
          </button>
          <button
            onClick={() => toast.error("Discount & Extra charge not yet implemented")}
            className={btn("secondary", "sm")}
          >
            Discount &amp; Extra charge
          </button>
          <button
            onClick={handleEdit}
            disabled={isInvoiced}
            title={isInvoiced ? "Cannot edit an invoiced receivable" : undefined}
            className={btn("primary", "sm")}
          >
            Update
          </button>
          <button
            onClick={() => !isInvoiced && setShowDeleteModal(true)}
            disabled={isInvoiced}
            title={isInvoiced ? "Cannot delete an invoiced receivable" : undefined}
            className={btn("danger", "sm")}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Invoiced notice */}
      {isInvoiced && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          This receivable has been invoiced (
          <Link
            to={`/client/invoice/${data.invoice.id}`}
            className="font-semibold underline hover:text-amber-900"
          >
            {data.invoice.invoice_code}
          </Link>
          ) and cannot be edited or deleted.
        </div>
      )}

      {/* Detail card */}
      <div className="bg-white shadow rounded-lg p-8 space-y-4 text-sm">
        <Field label="Invoice">
          {data.invoice ? (
            <Link
              to={`/client/invoice/${data.invoice.id}`}
              className="text-brand-600 hover:text-brand-800 underline font-medium"
            >
              {data.invoice.invoice_code}
            </Link>
          ) : (
            "—"
          )}
        </Field>
        <Field label="Project client">
          {client?.id ? (
            <Link
              to={`/clients/${client.id}`}
              className="text-brand-600 hover:text-brand-800 underline"
            >
              {clientName}
            </Link>
          ) : (
            clientName
          )}
        </Field>

        <p className="text-left font-bold text-base text-gray-900 pt-2 pb-2">Service</p>

        <Field label="PO number" value={data.po_number || "—"} />
        <Field label="Service" value={data.service?.name || "—"} />
        <Field label="Language pair" value={langPair} />
        <Field label="Project specialization" value={data.project?.specialization?.name || "—"} />
        {type === "unit_based" && (
          <>
            <Field label="Unit amount" value={fmtNum(data.unit_amount)} />
            <Field label="Unit" value={data.unit?.name || "—"} />
            <Field label="Price per unit" value={fmtNum(data.price_per_unit)} />
          </>
        )}

        <p className="text-left font-bold text-base text-gray-900 pt-2 pb-2">Subtotal &amp; total</p>

        <Field label="Subtotal" value={fmtNum(data.subtotal)} />
        <Field label="Extra charge" value="—" />
        <Field label="Discount" value="—" />
        <Field label="Total" value={fmtNum(data.subtotal)} />
        <Field label="Currency" value={currCode} />

        <p className="text-left font-bold text-base text-gray-900 pt-2 pb-2 pb-2">Other details</p>

        <Field label="Filename" value={data.file?.file_name || "—"} />
        <Field label="Note for client" value="—" />
        <Field label="Internal note" value={data.internal_note || "—"} />
        <Field label="Issued at" value={fmtDate(data.createdAt)} />
        <Field label="Created at" value={fmtDate(data.createdAt)} />
      </div>

      {showDeleteModal && (
        <ConfirmModal
          title="Confirm Delete"
          message={`Are you sure you want to delete receivable ${code}?`}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          confirmText="Yes, Delete"
        />
      )}
    </div>
  );
};

export default ReceivableDetailPage;
